/**
 * Individual PO Line row component for the PO Lines table.
 * Renders dynamic column cells based on visibility configuration.
 */

import {
  AlertCircle,
  Check,
  XCircle,
  Pause,
  Play,
  Edit,
  FileSearch,
} from 'lucide-react';
import { ConcessionStatusBadge } from '../../ConcessionStatusBadge';
import { PurchaseOrderLine, ColumnConfig, NextActionResult } from './types';
import { ProgressCell } from './ProgressCell';
import { getAcknowledgeStatusBadge, canReleaseHold, isFullyInSupplierWarehouse } from './utils';
import type { User } from '../../../../lib/api';
import { useFormatters } from '../../../../contexts/PreferencesContext';
import { useMenuSettings } from '../../../../contexts/MenuSettingsContext';
import { getOtifDateVariance } from '../../../../utils/otifDateVariance';

/** Human-readable hint for raw ERP order-type codes (shown as a tooltip). */
const ERP_ORDER_TYPE_LABELS: Record<string, string> = {
  '1': 'Standard / spot PO',
  '4': 'Scheduling agreement',
};

interface POLineRowProps {
  line: PurchaseOrderLine;
  user: User | null;
  columns: ColumnConfig[];
  selectedLineIds: Set<number>;
  formatDate: (date: string | Date, options?: { format?: string; includeTime?: boolean }) => string;
  formatCurrency: (value: number, currency?: string, showDecimals?: boolean | number) => string;
  shouldShowActions: (line: PurchaseOrderLine) => boolean;
  shouldShowDecline: (line: PurchaseOrderLine) => boolean;
  shouldShowProposeChange: (line: PurchaseOrderLine) => boolean;
  getNextAction: (line: PurchaseOrderLine) => NextActionResult;
  canSelectForBulkAccept: (line: PurchaseOrderLine) => boolean;
  onSelectLine: (lineId: number) => void;
  onReviewChanges: (lineId: number) => void;  // CR-4728
  onSelectLineForWorkflow: (line: PurchaseOrderLine, modal: 'accept' | 'decline') => void;
  onProposeChange: (line: PurchaseOrderLine) => void;
  onToggleCritical: (line: PurchaseOrderLine) => void;
  onPutOnHold: (line: PurchaseOrderLine) => void;
  onReleaseHold: (line: PurchaseOrderLine) => void;
  onToggleSelect: (lineId: number) => void;
  scheduleCollabEnabled?: boolean;
  // CR-4687 round 3: when ``true``, two or more rows are selected so per-line
  // action controls (Accept / Decline / ReviewChanges / Propose Change /
  // Put on hold / Release hold) are rendered disabled. Bulk actions are the
  // only allowed path in this mode — prevents a tester from acknowledging
  // one line while three are checked, which previously left the selection
  // count out of sync with the underlying state.
  bulkMode?: boolean;
}

export function POLineRow({
  line,
  user,
  columns,
  selectedLineIds,
  formatDate,
  formatCurrency,
  shouldShowActions,
  shouldShowDecline,
  shouldShowProposeChange,
  getNextAction,
  canSelectForBulkAccept,
  onSelectLine,
  onReviewChanges,
  onSelectLineForWorkflow,
  onProposeChange,
  onToggleCritical: _onToggleCritical,
  onPutOnHold,
  onReleaseHold,
  onToggleSelect,
  scheduleCollabEnabled = false,
  bulkMode = false,
}: POLineRowProps) {
  const bulkModeTitle = 'Disabled — multiple lines selected. Use bulk actions or clear selection.';
  const nextAction = getNextAction(line);
  // Conv. columns are held in the customer's reporting currency
  // (line.converted_currency — INR for an INR-based tenant, GBP for most).
  // formatCurrencyFromBase converts them into the user's preferred currency
  // when a rate exists, else labels them in the reporting currency.
  const { formatCurrencyFromBase, formatQuantity } = useFormatters();
  const formatConverted = (value: string) =>
    formatCurrencyFromBase(parseFloat(value), undefined, line.converted_currency);
  const { isFeatureEnabled } = useMenuSettings();

  // Supplier-side line closure: when the supplier has uploaded enough
  // warehouse stock to cover the outstanding qty, the line is greyed out
  // and supplier actions are suppressed. Gated on call-off because
  // suppliers without it have no warehouse-allocation concept. Customer
  // view is unaffected — they still need to GRN to close the order.
  const isSupplier = user?.platform === 'SUPPLIER';
  const inSupplierStock = isSupplier
    && isFeatureEnabled('switch_call_off_tab')
    && isFullyInSupplierWarehouse(line);

  const renderCell = (columnId: string) => {
    switch (columnId) {
      case 'acknowledge_status':
        return (
          <td key={columnId} className="py-3 px-4">
            {/* CR-4696: Show "Closed" for fully received lines */}
            {line.status === 'received' ? (
              <span className="px-2 py-1 rounded-full text-xs font-medium bg-gray-200 text-gray-600">Closed</span>
            ) : line.on_hold ? (
              <span className="px-2 py-1 rounded-full text-xs font-medium bg-orange-500 text-white">On Hold</span>
            ) : (
              getAcknowledgeStatusBadge(line.acknowledge_status || '')
            )}
          </td>
        );

      case 'action': {
        const isCustomerUser = user?.platform === 'CUSTOMER' || user?.platform === 'HELIX';
        const hasChangeProposed = line.acknowledge_status === 'change_proposed_supplier' || line.acknowledge_status === 'change_proposed_customer';
        return (
          <td key={columnId} className="py-3 px-4 text-center">
            <div className="flex items-center justify-center gap-2">
              {/* CR-4728: Customer users see a single "Review Changes" icon for proposed changes.
                  Opens the PO Line popup directly on the Change Requests tab. */}
              {isCustomerUser && hasChangeProposed ? (
                <button onClick={(e) => { e.stopPropagation(); onReviewChanges(line.id); }} disabled={bulkMode} className={`p-1 rounded transition-colors ${bulkMode ? 'opacity-40 cursor-not-allowed' : 'hover:bg-amber-50'}`} title={bulkMode ? bulkModeTitle : 'Review Changes'}>
                  <FileSearch className="h-5 w-5 text-amber-600" />
                </button>
              ) : (
                <>
                  {shouldShowActions(line) && (
                    <>
                      <button onClick={(e) => { e.stopPropagation(); onSelectLineForWorkflow(line, 'accept'); }} disabled={bulkMode} className={`p-1 rounded transition-colors ${bulkMode ? 'opacity-40 cursor-not-allowed' : 'hover:bg-green-50'}`} title={bulkMode ? bulkModeTitle : 'Accept'}>
                        <Check className="h-5 w-5 text-green-600" />
                      </button>
                      {shouldShowDecline(line) && (
                        <button onClick={(e) => { e.stopPropagation(); onSelectLineForWorkflow(line, 'decline'); }} disabled={bulkMode} className={`p-1 rounded transition-colors ${bulkMode ? 'opacity-40 cursor-not-allowed' : 'hover:bg-red-50'}`} title={bulkMode ? bulkModeTitle : 'Decline'}>
                          <XCircle className="h-5 w-5 text-red-600" />
                        </button>
                      )}
                    </>
                  )}
                  {shouldShowProposeChange(line) && (
                    <button onClick={(e) => { e.stopPropagation(); onProposeChange(line); }} disabled={bulkMode} className={`p-1 rounded transition-colors ${bulkMode ? 'opacity-40 cursor-not-allowed' : 'hover:bg-blue-50'}`} title={bulkMode ? bulkModeTitle : 'Propose Change'}>
                      <Edit className="h-5 w-5 text-blue-600" />
                    </button>
                  )}
                  {!shouldShowActions(line) && !shouldShowProposeChange(line) && (
                    <span className="text-xs text-muted-foreground">-</span>
                  )}
                </>
              )}
            </div>
          </td>
        );
      }

      case 'is_critical':
        return (
          <td key={columnId} className="py-3 px-4 text-center">
            {line.is_critical ? (
              <span className="p-1.5 rounded bg-amber-100 inline-flex" title="Critical">
                <AlertCircle className="h-5 w-5 text-amber-600" />
              </span>
            ) : (
              <span className="text-muted-foreground">-</span>
            )}
          </td>
        );

      case 'on_hold':
        return (
          <td key={columnId} className="py-3 px-4 text-center">
            {line.on_hold ? (
              // Only the side that placed the hold gets the release control at
              // all. A disabled button reads as "broken" — the other side has
              // no action to take here, so show a static On Hold indicator
              // naming who holds it instead.
              canReleaseHold(user, line) ? (
                <button
                  onClick={(e) => { e.stopPropagation(); onReleaseHold(line); }}
                  className={`p-1.5 rounded transition-colors ${bulkMode ? 'bg-green-100 opacity-40 cursor-not-allowed' : 'bg-green-100 hover:bg-green-200'}`}
                  title={bulkMode ? bulkModeTitle : 'Release from hold'} disabled={bulkMode}
                >
                  <Play className="h-4 w-4 text-green-700" />
                </button>
              ) : (
                <span
                  className="inline-flex p-1.5 rounded bg-muted"
                  title={`On hold by the ${line.on_hold_by_company_type === 'supplier' ? 'supplier' : 'customer'} — only they can release it`}
                >
                  <Pause className="h-4 w-4 text-muted-foreground" />
                </span>
              )
            ) : (
              <button onClick={(e) => { e.stopPropagation(); onPutOnHold(line); }} disabled={bulkMode} className={`p-1.5 rounded transition-colors ${bulkMode ? 'bg-orange-100 opacity-40 cursor-not-allowed' : 'bg-orange-100 hover:bg-orange-200'}`} title={bulkMode ? bulkModeTitle : 'Put on hold'}>
                <Pause className="h-4 w-4 text-orange-700" />
              </button>
            )}
          </td>
        );

      case 'hold_reason':
        return (
          <td key={columnId} className="py-3 px-4">
            {line.on_hold ? (
              <div className="text-sm">{line.on_hold_reason_text || line.on_hold_reason_details?.description || 'On hold'}</div>
            ) : (
              <span className="text-xs text-muted-foreground">-</span>
            )}
          </td>
        );

      case 'next_action':
        return (
          <td key={columnId} className="py-3 px-4">
            <div className="flex justify-center">
              <span className={`px-3 py-1 rounded-full text-xs font-medium ${nextAction.className}`}>{nextAction.text}</span>
            </div>
          </td>
        );

      case 'concession_status':
        return (
          <td key={columnId} className="py-3 px-4">
            <div className="flex justify-center">
              <ConcessionStatusBadge status={line.concession_status} compact />
            </div>
          </td>
        );

      case 'po_number':
        return (<td key={columnId} className="py-3 px-4"><span className="font-medium text-sm">{line.po_number || `PO-${line.po}`}</span></td>);

      case 'line_number':
        return (<td key={columnId} className="py-3 px-4 text-center"><span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-blue-100 text-blue-800 text-xs font-medium">{line.line_number}</span></td>);

      case 'erp_order_type':
        return (
          <td key={columnId} className="py-3 px-4 text-sm">
            {line.erp_order_type
              ? <span className="px-2 py-1 bg-muted rounded text-xs font-medium" title={ERP_ORDER_TYPE_LABELS[line.erp_order_type] || `ERP type ${line.erp_order_type}`}>{line.erp_order_type}</span>
              : <span className="text-muted-foreground text-xs">-</span>}
          </td>
        );

      case 'supplier_name':
        return (
          <td key={columnId} className="py-3 px-4 text-sm">
            {/* CR-4717: Plain text, no hyperlink */}
            <span>{line.supplier_name || 'N/A'}</span>
          </td>
        );

      case 'customer_name':
        return (<td key={columnId} className="py-3 px-4 text-sm">{line.customer_name || 'N/A'}</td>);
      case 'part_number':
        return (<td key={columnId} className="py-3 px-4"><span className="font-mono text-sm font-medium">{line.part_number}</span></td>);
      case 'description':
        return (<td key={columnId} className="py-3 px-4"><div className="text-sm">{line.description}</div></td>);
      case 'quantity':
        return (<td key={columnId} className="py-3 px-4 text-right"><span className="text-sm font-medium">{formatQuantity(line.quantity)}</span></td>);
      case 'received_qty':
        return (<td key={columnId} className="py-3 px-4 text-right"><span className="text-sm font-medium">{formatQuantity(line.received_qty)}</span></td>);
      case 'delivered_warehouse_qty': {
        const warehouse = parseFloat(line.delivered_warehouse_qty || '0') || 0;
        return (
          <td key={columnId} className="py-3 px-4 text-right">
            <span className={`text-sm font-medium ${warehouse > 0 ? 'text-blue-600' : 'text-muted-foreground'}`}>
              {formatQuantity(warehouse)}
            </span>
          </td>
        );
      }
      case 'warehouse_fulfilment_pct': {
        // CR-7163 (a): supplier-side Fulfilment % — of what this line still
        // owes, how much is sitting in the supplier's own warehouse ready to
        // ship. Numerator is warehouse stock only; the customer's receipts are
        // deliberately excluded (they already have "% Delivered" for that).
        //
        // barPercent floors, so 100% appears only when warehouse >= outstanding
        // — exactly the grey-out threshold (isFullyInSupplierWarehouse). The
        // bar and the row state therefore cannot disagree.
        return (
          <ProgressCell
            key={columnId}
            value={parseFloat(line.delivered_warehouse_qty || '0') || 0}
            total={parseFloat(line.outstanding_qty || '0') || 0}
            emptyLabel="—"
          />
        );
      }
      case 'outstanding_qty':
        return (<td key={columnId} className="py-3 px-4 text-right"><span className={`text-sm font-medium ${parseFloat(line.outstanding_qty) > 0 ? 'text-orange-600' : 'text-muted-foreground'}`}>{formatQuantity(line.outstanding_qty)}</span></td>);
      case 'pct_delivered':
        return (
          <ProgressCell
            key={columnId}
            value={parseFloat(line.received_qty) || 0}
            total={parseFloat(line.quantity) || 0}
          />
        );
      case 'unit_price':
        return (<td key={columnId} className="py-3 px-4 text-right text-sm">{formatCurrency(parseFloat(line.unit_price) || 0, line.currency, 2)}</td>);
      case 'total_price':
        return (<td key={columnId} className="py-3 px-4 text-right font-medium">{formatCurrency(parseFloat(line.total_price) || 0, line.currency, 2)}</td>);
      case 'delivery_date': {
        // "Agreed Date (ODD)": the server's odd_date, not the stored delivery_date.
        const odd = line.odd_date ?? line.delivery_date;
        return (<td key={columnId} className="py-3 px-4 text-sm">{odd ? formatDate(odd) : '-'}</td>);
      }
      case 'supplier_promise_date': {
        // CR-5479: amber when supplier-proposed date is earlier than the
        // ODD's OTIF window allows; red when it's later. Shared with the
        // Change Request views via getOtifDateVariance so the table and the
        // drill-down colour/variance stay identical.
        if (!line.supplier_promise_date) {
          return (<td key={columnId} className="py-3 px-4 text-sm"></td>);
        }
        const variance = getOtifDateVariance(
          line.supplier_promise_date, line.odd_date ?? line.delivery_date, line.supplier_otif_window,
        );
        const cls = variance?.className || '';
        const suffix = variance?.suffix || '';
        return (
          <td key={columnId} className="py-3 px-4 text-sm">
            <span className={cls ? `inline-block px-2 py-0.5 rounded ${cls}` : ''}>
              {formatDate(line.supplier_promise_date)}{suffix}
            </span>
          </td>
        );
      }
      case 'buyer_id':
        return (<td key={columnId} className="py-3 px-4 text-sm">{line.buyer_id || '-'}</td>);
      case 'customer_name':
        return (<td key={columnId} className="py-3 px-4 text-sm">{line.customer_name || '-'}</td>);
      case 'currency':
        return (<td key={columnId} className="py-3 px-4 text-sm text-center"><span className="px-2 py-1 bg-muted rounded text-xs font-medium">{line.currency || '-'}</span></td>);
      case 'batch_serial_no':
        return (<td key={columnId} className="py-3 px-4 text-sm font-mono">{line.batch_serial_no || '-'}</td>);
      case 'ppv': {
        // CR-5957 round 5: legacy OpenOrdersSQL.cs:230 distinguishes PPV=NULL
        // (no contract match → blank) from PPV=0 (contract matched, no variance
        // → render the literal value). Previous check `parseFloat(line.ppv) !== 0`
        // collapsed both into a dash, making a contract-matched-zero-variance
        // line look identical to a no-contract line.
        const ppvNum = line.ppv != null && line.ppv !== '' ? parseFloat(line.ppv) : null;
        return (
          <td key={columnId} className="py-3 px-4 text-sm text-right">
            {ppvNum === null || Number.isNaN(ppvNum) ? (
              '-'
            ) : ppvNum === 0 ? (
              // Legacy OpenOrdersSQL.cs:230 returns PPV as a plain numeric
              // string with no currency symbol — PPV is a variance KPI, not
              // a price. Render decimals only.
              <span className="inline-block px-2 py-0.5 rounded bg-muted text-foreground font-medium">
                {new Intl.NumberFormat('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(0)}
              </span>
            ) : (
              <span className={`inline-block px-2 py-0.5 rounded text-white font-medium ${ppvNum > 0 ? 'bg-red-500' : 'bg-amber-500'}`}>
                {new Intl.NumberFormat('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(ppvNum)}
              </span>
            )}
          </td>
        );
      }
      case 'otif_overridden':
        return (
          <td key={columnId} className="py-3 px-4 text-center">
            {line.otif_overridden ? (<span className="px-2 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-800">Yes</span>) : (<span className="text-muted-foreground">-</span>)}
          </td>
        );
      case 'supplier_group_name':
        return (<td key={columnId} className="py-3 px-4 text-sm">{line.supplier_group_name || '-'}</td>);
      case 'packing_note_number':
        return (<td key={columnId} className="py-3 px-4 text-sm font-mono">{line.packing_note_number || '-'}</td>);
      case 'order_date':
        return (<td key={columnId} className="py-3 px-4 text-sm">{line.order_date ? formatDate(line.order_date) : '-'}</td>);
      case 'last_received_date':
        return (<td key={columnId} className="py-3 px-4 text-sm">{line.last_received_date ? formatDate(line.last_received_date) : '-'}</td>);
      case 'updated_at':
        return (<td key={columnId} className="py-3 px-4 text-sm">{line.updated_at ? formatDate(line.updated_at, { includeTime: true }) : '-'}</td>);
      case 'required_date_change_count':
        return (
          <td key={columnId} className="py-3 px-4 text-center">
            {line.required_date_change_count !== undefined && line.required_date_change_count > 0 ? (
              <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-blue-100 text-blue-800 text-xs font-medium">{line.required_date_change_count}</span>
            ) : (<span className="text-muted-foreground">-</span>)}
          </td>
        );
      case 'promised_date_change_count':
        return (
          <td key={columnId} className="py-3 px-4 text-center">
            {line.promised_date_change_count !== undefined && line.promised_date_change_count > 0 ? (
              <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-purple-100 text-purple-800 text-xs font-medium">{line.promised_date_change_count}</span>
            ) : (<span className="text-muted-foreground">-</span>)}
          </td>
        );
      case 'pdf_attached_count':
        return (
          <td key={columnId} className="py-3 px-4 text-center">
            {line.pdf_attached_count !== undefined && line.pdf_attached_count > 0 ? (
              <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-green-100 text-green-800 text-xs font-medium">{line.pdf_attached_count}</span>
            ) : (<span className="text-muted-foreground">-</span>)}
          </td>
        );
      case 'material_cert_count':
        return (
          <td key={columnId} className="py-3 px-4 text-center">
            {line.material_cert_count !== undefined && line.material_cert_count > 0 ? (
              <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-teal-100 text-teal-800 text-xs font-medium">{line.material_cert_count}</span>
            ) : (<span className="text-muted-foreground">-</span>)}
          </td>
        );
      case 'contract_unit_price':
        return (<td key={columnId} className="py-3 px-4 text-right text-sm">{line.contract_unit_price ? formatCurrency(parseFloat(line.contract_unit_price), line.currency, 3) : '-'}</td>);
      case 'contract_lot_price':
        return (<td key={columnId} className="py-3 px-4 text-right text-sm">{line.contract_lot_price ? formatCurrency(parseFloat(line.contract_lot_price), line.currency, 3) : '-'}</td>);
      case 'drawing_number':
        return (<td key={columnId} className="py-3 px-4 text-sm font-mono">{line.drawing_number || '-'}</td>);
      case 'drawing_version':
        return (<td key={columnId} className="py-3 px-4 text-sm">{line.drawing_version || '-'}</td>);
      case 'outstanding_value':
        return (
          <td key={columnId} className="py-3 px-4 text-right text-sm">
            {line.outstanding_value ? (<span className={parseFloat(line.outstanding_value) > 0 ? 'text-orange-600 font-medium' : ''}>{formatCurrency(parseFloat(line.outstanding_value), line.currency, 3)}</span>) : '-'}
          </td>
        );
      case 'converted_unit_price':
        return (<td key={columnId} className="py-3 px-4 text-right text-sm">{line.converted_unit_price ? formatConverted(line.converted_unit_price) : '-'}</td>);
      case 'converted_total_value':
        return (<td key={columnId} className="py-3 px-4 text-right text-sm font-medium">{line.converted_total_value ? formatConverted(line.converted_total_value) : '-'}</td>);
      case 'converted_outstanding_value':
        return (
          <td key={columnId} className="py-3 px-4 text-right text-sm">
            {line.converted_outstanding_value ? (<span className={parseFloat(line.converted_outstanding_value) > 0 ? 'text-orange-600 font-medium' : ''}>{formatConverted(line.converted_outstanding_value)}</span>) : '-'}
          </td>
        );
      case 'original_promise_date':
        return (<td key={columnId} className="py-3 px-4 text-sm">{line.original_promise_date ? formatDate(line.original_promise_date) : '-'}</td>);
      case 'mrp_scheduled_date':
        return (<td key={columnId} className="py-3 px-4 text-sm">{line.mrp_scheduled_date ? formatDate(line.mrp_scheduled_date) : '-'}</td>);
      case 'priority':
        return (
          <td key={columnId} className="py-3 px-4 text-center">
            {line.priority ? (
              <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                line.priority === 'critical' ? 'bg-red-100 text-red-800' :
                line.priority === 'high' ? 'bg-orange-100 text-orange-800' :
                line.priority === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                'bg-gray-100 text-gray-800'
              }`}>{line.priority.charAt(0).toUpperCase() + line.priority.slice(1)}</span>
            ) : (<span className="text-muted-foreground">-</span>)}
          </td>
        );
      case 'contract_price':
        return (<td key={columnId} className="py-3 px-4 text-right text-sm">{line.contract_price ? formatCurrency(parseFloat(line.contract_price), line.currency) : '-'}</td>);
      case 'exchange_rate':
        return (<td key={columnId} className="py-3 px-4 text-right text-sm font-mono">{line.exchange_rate ? parseFloat(line.exchange_rate).toFixed(6) : '-'}</td>);
      default:
        return null;
    }
  };

  return (
    <tr
      className={`border-b hover:bg-muted/50 cursor-pointer transition-colors ${line.status === 'received' || line.status === 'cancelled' || line.acknowledge_status === 'closed' ? 'opacity-50 bg-muted/30' : inSupplierStock ? 'opacity-60 bg-slate-50' : ''} ${line.is_forecast ? 'bg-amber-50/50' : ''}`}
      title={line.is_forecast ? `Forecast — outside firm window (${line.firm_days ?? '?'} days)` : inSupplierStock ? 'In Stock — full ordered qty is in your warehouse, awaiting customer GRN.' : undefined}
      onClick={() => onSelectLine(line.id)}
    >
      {!scheduleCollabEnabled && (
        <td className="py-3 px-2 text-center" onClick={(e) => e.stopPropagation()}>
          {canSelectForBulkAccept(line) ? (
            <input type="checkbox" checked={selectedLineIds.has(line.id)} onChange={() => onToggleSelect(line.id)} className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary" />
          ) : (
            <span className="text-muted-foreground">-</span>
          )}
        </td>
      )}
      {columns.filter(col => col.visible).map(col => renderCell(col.id))}
      <td className="py-3 px-4 text-center"><span className="text-xs text-primary hover:underline">View →</span></td>
    </tr>
  );
}
